import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {
  InsightsFeature,
  InventoryFeature,
  LabAutomationFeature,
  ProcessesFeature,
  RegistryFeature,
} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled, isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section, SECTIONS} from 'constants/NavConstants';
import DevToolsIcon from 'dev/DevToolsIcon';
import {HomeDashboardStoreState} from 'home-dashboard/HomeDashboardStore';
import {isNavbarColored, makeNavbarBackgroundStyles} from 'nav/navbar/NavBarColor';
// @ts-expect-error
import AnchorModalsForOnboarding from 'onboarding/AnchorModalsForOnboarding';
import ChecklistMenuIcon from 'onboarding/ChecklistMenuIcon';
import {shouldShowOnboardingChecklistsIcon} from 'onboarding/OnboardingUtils';
import UpgradeAccountModal, {ENTERPRISE_FEATURES_KEYS} from 'start/upgrade-account/UpgradeAccountModal';
import {ITEM_SIZE_SIDE_NAVBAR, ZINDEX_SIDE_NAVBAR} from 'styles/StyleConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {ElementMouseEvent} from 'util/Types';

import CreateIcon from './CreateIcon';
import CreateMenu from './CreateMenu';
import HomeIcon from './HomeIcon';
import NavBarIcon from './NavBarIcon';
import RegistryIcon from './RegistryIcon';
import UserIcon from './UserIcon';

interface NavBarContentsProps {
  currentUser: EntityStoreUser;
  homeDashboardState: HomeDashboardStoreState;
  isCreateMenuOpen: boolean;
  isUserIconActive: boolean;
  onSectionClick: (e: ElementMouseEvent, section: Section) => void;
  section: Section | null;
}

export default function NavBarContents(props: NavBarContentsProps): JSX.Element {
  const isHomeIconActive =
    props.section === SECTIONS.HOME ||
    (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS &&
      (props.section === SECTIONS.WORKLISTS || props.section === SECTIONS.WORKLIST_ITEMS));

  return (
    <div
      className={classNames(
        css(navBarContentsStyles.navbar, window.impersonating && navBarContentsStyles.impersonating),
        'js-navTour-nav _test-navBarContents'
      )}
      style={makeNavbarBackgroundStyles(false)}
      {...getTestProps({component: 'NavBarContents', element: 'root'})}
    >
      <div>
        <HomeIcon
          currentUser={props.currentUser}
          icon="benchling"
          isActive={isHomeIconActive}
          onHomeIconClick={props.onSectionClick}
        />
        <NavBarIcon
          icon={ModelTypeConstants.SOURCE}
          isActive={props.section === SECTIONS.PROJECT_ITEMS || props.section === SECTIONS.PROJECTS}
          onSectionClick={props.onSectionClick}
          section={SECTIONS.PROJECTS}
          className="_test-navDropdownMenu-item-projects"
        />
        {BenchlingConfig.STUDIES_ENABLE && (
          <NavBarIcon
            icon={ModelTypeConstants.STUDY}
            isActive={props.section === SECTIONS.STUDIES}
            onSectionClick={props.onSectionClick}
            section={SECTIONS.STUDIES}
          />
        )}
        <NavBarIcon
          icon="search"
          isActive={props.section === SECTIONS.SEARCH}
          onSectionClick={props.onSectionClick}
          section={SECTIONS.SEARCH}
        />
        <CreateIcon createMenu={<CreateMenu />} isCreateMenuOpen={props.isCreateMenuOpen} />
        <div className={css(navBarContentsStyles.spacer)} />
        {isFeatureEnabled({feature: RegistryFeature.REGISTRY_DISCOVER}) && (
          <RegistryIcon
            currentUser={props.currentUser}
            onSectionClick={props.onSectionClick}
            isActive={props.section === SECTIONS.REGISTRIES}
            section={SECTIONS.REGISTRIES}
          />
        )}
        {isFeatureEnabled({feature: InventoryFeature.INVENTORY_DISCOVER}) && (
          <NavBarIcon
            icon={ModelTypeConstants.LOCATION}
            isActive={props.section === SECTIONS.STORAGE}
            onSectionClick={props.onSectionClick}
            section={SECTIONS.STORAGE}
          />
        )}
        {(() => {
          if (BenchlingConfig.LEGACY_WORKFLOWS_ENABLE) {
            return (
              <NavBarIcon
                icon="workflow"
                isActive={props.section === SECTIONS.WORKFLOWS}
                onSectionClick={props.onSectionClick}
                section={SECTIONS.WORKFLOWS}
              />
            );
          } else if (UpgradeAccountModal.isEnabled(props.currentUser)) {
            return (
              <NavBarIcon
                icon="workflow"
                className={css(UpgradeAccountModal.hasRequested() && navBarContentsStyles.disabledIcon)}
                isActive={props.section === SECTIONS.WORKFLOWS}
                onSectionClick={() => {
                  UpgradeAccountModal.open(ENTERPRISE_FEATURES_KEYS.WORKFLOWS);
                }}
                section={SECTIONS.WORKFLOWS}
              />
            );
          }
          return null;
        })()}
        {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_DISCOVER}) && (
          <NavBarIcon
            icon="process"
            isActive={props.section === SECTIONS.PROCESSES}
            onSectionClick={props.onSectionClick}
            section={SECTIONS.PROCESSES}
          />
        )}
        {(() => {
          if (BenchlingConfig.ENABLE_REQUESTS) {
            return (
              <NavBarIcon
                icon="request"
                isActive={props.section === SECTIONS.REQUESTS}
                onSectionClick={props.onSectionClick}
                section={SECTIONS.REQUESTS}
              />
            );
          } else if (UpgradeAccountModal.isEnabled(props.currentUser)) {
            return (
              <NavBarIcon
                icon="request"
                className={css(UpgradeAccountModal.hasRequested() && navBarContentsStyles.disabledIcon)}
                isActive={props.section === SECTIONS.REQUESTS}
                onSectionClick={() => {
                  UpgradeAccountModal.open(ENTERPRISE_FEATURES_KEYS.REQUESTS);
                }}
                section={SECTIONS.REQUESTS}
              />
            );
          }
          return null;
        })()}
        {BenchlingConfig.REQUESTS_V2_ENABLE && BenchlingConfig.REQUESTS_V2_MVP_ENABLE && (
          <NavBarIcon
            icon="request_v2_submission"
            isActive={
              props.section != null &&
              [SECTIONS.REQUESTS_V2_SUBMISSIONS, SECTIONS.REQUESTS_V2_DEFINITIONS].includes(props.section)
            }
            onSectionClick={props.onSectionClick}
            // TODO (BNCH-111782): Open the last tab the user was on
            section={SECTIONS.REQUESTS_V2_DEFINITIONS}
          />
        )}
        {isFeatureEnabled({feature: InsightsFeature.INSIGHTS_DISCOVER}) && (
          <NavBarIcon
            icon="analyze"
            isActive={props.section === SECTIONS.ANALYTICS}
            onSectionClick={props.onSectionClick}
            section={SECTIONS.ANALYTICS}
          />
        )}
        {(BenchlingConfig.TFI_ENABLE ||
          isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})) && (
          <NavBarIcon
            icon="external-files"
            isActive={
              props.section === SECTIONS.TFI ||
              props.section === SECTIONS.BENCHLING_APPS ||
              props.section === SECTIONS.CONNECTIONS
            }
            onSectionClick={props.onSectionClick}
            section={
              isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_DISCOVER})
                ? SECTIONS.CONNECTIONS
                : SECTIONS.TFI
            }
          />
        )}
        {BenchlingConfig.APP_CORE_ENABLE_FAKE_ITEM_TYPE && (
          <NavBarIcon
            icon="core-object"
            isActive={props.section === SECTIONS.JELLYFISH}
            onSectionClick={props.onSectionClick}
            section={SECTIONS.JELLYFISH}
          />
        )}
        {window.debug && <DevToolsIcon withUserMenuOffset={true} />}
        {shouldShowOnboardingChecklistsIcon() && (
          <ChecklistMenuIcon shouldAlignBottom={!window.debug} shouldShowNotification={true} />
        )}
        <UserIcon currentUser={props.currentUser} isActive={props.isUserIconActive} />
        {props.homeDashboardState && <AnchorModalsForOnboarding {...props.homeDashboardState} />}
      </div>
    </div>
  );
}

NavBarContents.defaultProps = {
  isCreateMenuOpen: false,
  isUserIconActive: false,
};

export const navBarContentsStyles = StyleSheet.create({
  navbar: {
    ...(!isNavbarColored() && {backgroundColor: Colors.BLUE}),
    bottom: 0,
    position: 'absolute',
    top: 0,
    width: ITEM_SIZE_SIDE_NAVBAR,
    zIndex: ZINDEX_SIDE_NAVBAR,
  },
  impersonating: {
    backgroundColor: Colors.RED_LIGHT,
  },
  disabledIcon: {
    color: Colors.WHITE,
    opacity: 0.4,
  },
  spacer: {
    height: 20,
  },
});
